import { ContentStore } from '../api/ContentStore';
import { DateTime, Duration } from 'luxon';
import { GroupDateMapper } from '../utils/GroupDateMapper';
import { useQuery } from '@tanstack/react-query';
import { marked } from 'marked';

export type ScienceSomethingContent = {
  content: { title: string; body: string; offset: number; duration: number };
};

export class GetScienceSomethingViewCreator {
  constructor(private readonly content: ContentStore = new ContentStore()) {}

  public async CreateView(groupStart: DateTime, groupId: number): Promise<ScienceSomethingContent> {
    const contentItems = await this.content.getScienceSomethingContent(groupId, (item) => {
      return {
        title: String(item.title),
        body: marked(item.body),
        offset: Number(item.offset),
        duration: Number(item.duration),
      };
    });
    const dateMapper = new GroupDateMapper(groupStart as DateTime<true>);
    const date = DateTime.local().toISODate();
    const currentOffset = dateMapper.day(date);

    // The end of the window is exclusive. With `>=`, consecutive 7-day entries
    // overlapped on the changeover day — day 19 satisfied both 12..19 and 19..26 —
    // and `find` over an unordered list then returned whichever came first, so the
    // previous week's content showed on the Friday a new one was due.
    const info = contentItems.find((item) => {
      return item.offset <= currentOffset && item.offset + item.duration > currentOffset;
    });

    return { content: info } as ScienceSomethingContent;
  }
}

export const useGetScienceSomethingViewCreator = (groupStart: DateTime | undefined, groupId: number) =>
  useQuery({
    // Keyed on the cohort, as ExerciseInDisguiseContentViewCreator is. A constant
    // key meant the first cohort's content was reused for every other cohort within
    // the 10-minute staleTime, so switching teams showed the wrong Science Something.
    queryKey: [GetScienceSomethingViewCreator.name, groupStart, groupId],
    queryFn: () => new GetScienceSomethingViewCreator().CreateView(groupStart as DateTime, groupId),
    enabled: !!groupStart,
    staleTime: Duration.fromObject({ minutes: 10 }).milliseconds,
  });
